import { useState, useMemo, useCallback, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { ArrowPathIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import KPICard from '../components/KPICard';
import { useRetentionData } from '../hooks/useRetentionData';
import { fetchChurnReasonOptions, updateChurnReason } from '../services/retentionData';
import { formatNumber } from '../utils/formatters';
import { SF_BASE } from '../constants';
import type { ChurnAccount } from '../types/retention';

type SortDir = 'asc' | 'desc';

function useSortState<F extends string>(defaultField: F, defaultDir: SortDir = 'desc') {
  const [field, setField] = useState<F>(defaultField);
  const [dir, setDir] = useState<SortDir>(defaultDir);

  const toggle = useCallback(
    (f: F) => {
      if (field === f) {
        setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setField(f);
        setDir('desc');
      }
    },
    [field],
  );

  const SortIcon = useCallback(
    ({ f }: { f: F }) => {
      if (field !== f) return null;
      return dir === 'asc' ? (
        <ChevronUpIcon className="w-3 h-3 inline ml-0.5" />
      ) : (
        <ChevronDownIcon className="w-3 h-3 inline ml-0.5" />
      );
    },
    [field, dir],
  );

  return { field, dir, toggle, SortIcon };
}

function sortRows<T>(rows: T[], field: keyof T, dir: SortDir): T[] {
  return [...rows].sort((a, b) => {
    const av = a[field] ?? '';
    const bv = b[field] ?? '';
    if (typeof av === 'string' && typeof bv === 'string') {
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return dir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });
}

function SectionLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin h-6 w-6 border-3 border-gray-200 border-t-eep-navy rounded-full" />
    </div>
  );
}

function daysBadge(days: number): string {
  if (days > 90) return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

type ChurnSortField = 'name' | 'daysSinceActivity' | 'totalPlans' | 'lastActivityDate' | 'trustBasedCredits' | 'willBasedCredits' | 'churnReason';
type StaleUserSortField = 'name' | 'email' | 'accountName' | 'lastLogin' | 'daysSinceLogin';
type StaleAccountSortField = 'accountName' | 'lastLogin' | 'totalUsers' | 'daysSinceLogin';

const churnTableColumns: [ChurnSortField, string][] = [
  ['name', 'Account'],
  ['totalPlans', 'Total Plans'],
  ['trustBasedCredits', 'Trust Credits'],
  ['willBasedCredits', 'Will Credits'],
  ['churnReason', 'Churn Reason'],
  ['lastActivityDate', 'Last Activity'],
  ['daysSinceActivity', 'Days Inactive'],
];

function ChurnReasonSelect({
  accountId,
  value,
  options,
}: {
  accountId: string;
  value: string | null;
  options: string[];
}) {
  const [current, setCurrent] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // Reconcile with refreshed data when the underlying value changes.
  useEffect(() => {
    setCurrent(value ?? '');
  }, [value]);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      const prev = current;
      setCurrent(next);
      setSaving(true);
      setError(false);
      try {
        await updateChurnReason(accountId, next || null);
      } catch {
        setCurrent(prev);
        setError(true);
      } finally {
        setSaving(false);
      }
    },
    [accountId, current],
  );

  // Keep a value already set in Salesforce selectable even if it is no longer
  // an active picklist option.
  const allOptions = current && !options.includes(current) ? [current, ...options] : options;

  return (
    <select
      value={current}
      onChange={handleChange}
      disabled={saving}
      title={error ? 'Failed to save — try again' : current || 'Set churn reason'}
      className={`text-xs border rounded px-1.5 py-1 bg-white max-w-[11rem] focus:outline-none focus:ring-1 focus:ring-eep-navy ${
        error ? 'border-red-400 text-red-700' : 'border-gray-300 text-gray-700'
      } ${saving ? 'opacity-60 cursor-wait' : ''}`}
    >
      <option value="">—</option>
      {allOptions.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function ChurnTable({
  accounts,
  sort,
  label,
  churnReasonOptions,
}: {
  accounts: ChurnAccount[];
  sort: ReturnType<typeof useSortState<ChurnSortField>>;
  label: string;
  churnReasonOptions: string[];
}) {
  const sorted = useMemo(
    () => sortRows(accounts, sort.field, sort.dir),
    [accounts, sort.field, sort.dir],
  );

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-2">
        {label} ({accounts.length})
      </h3>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-gray-200 text-left text-gray-600">
              {churnTableColumns.map(([field, header]) => (
                <th
                  key={field}
                  className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                  onClick={() => sort.toggle(field)}
                >
                  {header}
                  <sort.SortIcon f={field} />
                </th>
              ))}
              <th className="pb-2 pr-4 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((acct) => (
              <tr key={acct.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 pr-4 font-medium text-gray-900">{acct.name}</td>
                <td className="py-2 pr-4">{acct.totalPlans}</td>
                <td className="py-2 pr-4">{acct.trustBasedCredits ?? '—'}</td>
                <td className="py-2 pr-4">{acct.willBasedCredits ?? '—'}</td>
                <td className="py-2 pr-4">
                  <ChurnReasonSelect
                    accountId={acct.id}
                    value={acct.churnReason}
                    options={churnReasonOptions}
                  />
                </td>
                <td className="py-2 pr-4">{acct.lastActivityDate ?? '—'}</td>
                <td className="py-2 pr-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      acct.daysSinceActivity >= 180
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {acct.daysSinceActivity}d
                  </span>
                </td>
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    {acct.eepFirmId && (
                      <a
                        href={`https://app.encorestateplans.com/home/account-user/view/${acct.eepFirmId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                      >
                        Encore
                      </a>
                    )}
                    <a
                      href={`${SF_BASE}/${acct.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      Salesforce
                    </a>
                    {acct.website && (
                      <a
                        href={acct.website.startsWith('http') ? acct.website : `https://${acct.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                      >
                        Website
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Retention() {
  const { churnAnalysis, staleUsers, staleAccounts, loading, error, refetch } = useRetentionData();

  const churnSort = useSortState<ChurnSortField>('daysSinceActivity');
  const preChurnSort = useSortState<ChurnSortField>('daysSinceActivity');
  const staleUserSort = useSortState<StaleUserSortField>('daysSinceLogin');
  const staleAccountSort = useSortState<StaleAccountSortField>('daysSinceLogin');

  const [churnReasonOptions, setChurnReasonOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetchChurnReasonOptions(controller.signal)
      .then((opts) => {
        if (!cancelled) setChurnReasonOptions(opts);
      })
      .catch(() => {
        // Non-fatal: the dropdown still renders existing values and allows clearing.
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const sortedStaleUsers = useMemo(
    () => (staleUsers ? sortRows(staleUsers.users, staleUserSort.field, staleUserSort.dir) : []),
    [staleUsers, staleUserSort.field, staleUserSort.dir],
  );

  const sortedStaleAccounts = useMemo(
    () =>
      staleAccounts
        ? sortRows(staleAccounts.accounts, staleAccountSort.field, staleAccountSort.dir)
        : [],
    [staleAccounts, staleAccountSort.field, staleAccountSort.dir],
  );

  if (loading && !churnAnalysis && !staleUsers && !staleAccounts) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-eep-navy rounded-full" />
      </div>
    );
  }

  if (error && !churnAnalysis && !staleUsers && !staleAccounts) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-eep-navy text-white rounded-lg hover:bg-eep-navy-light transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Retention</h1>
          <p className="text-sm text-gray-500 mt-1">
            Churn and stale user/account analysis
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="space-y-6">
        {/* ═══ CHURN & PRE-CHURN ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Churn Analysis</h2>
          <p className="text-xs text-gray-500 mb-4">
            Accounts with 7+ plans where all activity dates are older than the threshold.
            Churn = 180+ days inactive. Pre-Churn = 90–179 days inactive.
          </p>
          {!churnAnalysis ? (
            <SectionLoading />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
                <KPICard title="Churned" value={formatNumber(churnAnalysis.churn.total)} />
                <KPICard title="Pre-Churn" value={formatNumber(churnAnalysis.preChurn.total)} />
              </div>

              <div className="mb-6">
                <ChurnTable
                  accounts={churnAnalysis.churn.accounts}
                  sort={churnSort}
                  label="Churned Accounts (180+ days)"
                  churnReasonOptions={churnReasonOptions}
                />
              </div>

              <ChurnTable
                accounts={churnAnalysis.preChurn.accounts}
                sort={preChurnSort}
                label="Pre-Churn Accounts (90–179 days)"
                churnReasonOptions={churnReasonOptions}
              />
            </>
          )}
        </div>

        {/* ═══ STALE USERS ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stale Users</h2>
          {!staleUsers ? (
            <SectionLoading />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6 max-w-xs">
                <KPICard title="Total Stale Users" value={formatNumber(staleUsers.totalStaleUsers)} />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Users with no login in 45+ days ({staleUsers.users.length})
                </h3>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-gray-200 text-left text-gray-600">
                        {([
                          ['name', 'Name'],
                          ['email', 'Email'],
                          ['accountName', 'Account'],
                          ['lastLogin', 'Last Login'],
                          ['daysSinceLogin', 'Days Since Login'],
                        ] as [StaleUserSortField, string][]).map(([field, label]) => (
                          <th
                            key={field}
                            className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                            onClick={() => staleUserSort.toggle(field)}
                          >
                            {label}
                            <staleUserSort.SortIcon f={field} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStaleUsers.map((user) => (
                        <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 pr-4 font-medium text-gray-900">{user.name}</td>
                          <td className="py-2 pr-4 text-gray-600">{user.email}</td>
                          <td className="py-2 pr-4">{user.accountName}</td>
                          <td className="py-2 pr-4">{user.lastLogin}</td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${daysBadge(user.daysSinceLogin)}`}
                            >
                              {user.daysSinceLogin}d
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ═══ STALE ACCOUNTS ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stale Accounts</h2>
          {!staleAccounts ? (
            <SectionLoading />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6 max-w-xs">
                <KPICard
                  title="Total Stale Accounts"
                  value={formatNumber(staleAccounts.totalStaleAccounts)}
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Accounts with no user login in 45+ days ({staleAccounts.accounts.length})
                </h3>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-gray-200 text-left text-gray-600">
                        {([
                          ['accountName', 'Account Name'],
                          ['lastLogin', 'Last Login'],
                          ['totalUsers', 'Total Users'],
                          ['daysSinceLogin', 'Days Since Login'],
                        ] as [StaleAccountSortField, string][]).map(([field, label]) => (
                          <th
                            key={field}
                            className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                            onClick={() => staleAccountSort.toggle(field)}
                          >
                            {label}
                            <staleAccountSort.SortIcon f={field} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStaleAccounts.map((acct) => (
                        <tr key={acct.accountId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 pr-4 font-medium text-gray-900">{acct.accountName}</td>
                          <td className="py-2 pr-4">{acct.lastLogin}</td>
                          <td className="py-2 pr-4">{acct.totalUsers}</td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${daysBadge(acct.daysSinceLogin)}`}
                            >
                              {acct.daysSinceLogin}d
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
